import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCategories = () => {
  const { getAuthHeaders } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, category: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
  const [formData, setFormData] = useState({ name: '', slug: '', description: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    }
    setLoading(false);
  };

  const handleOpenCreate = () => {
    setFormData({ name: '', slug: '', description: '' });
    setEditDialog({ open: true, category: null });
  };

  const handleOpenEdit = (category) => {
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || ''
    });
    setEditDialog({ open: true, category });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editDialog.category) {
        await axios.put(
          `${API_URL}/api/categories/${editDialog.category.id}`,
          formData,
          getAuthHeaders()
        );
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/api/categories`, formData, getAuthHeaders());
        toast.success('Category created');
      }
      setEditDialog({ open: false, category: null });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.category) return;
    try {
      await axios.delete(
        `${API_URL}/api/categories/${deleteDialog.category.id}`,
        getAuthHeaders()
      );
      toast.success('Category deleted');
      setDeleteDialog({ open: false, category: null });
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleNameChange = (name) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    setFormData({ ...formData, name, slug });
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-[#2C2C2C]" data-testid="categories-title">Categories</h1>
            <p className="font-body text-[#767C91]">{categories.length} categories</p>
          </div>
          <Button 
            onClick={handleOpenCreate}
            className="bg-[#9D6E6D] hover:bg-[#2C2C2C] text-white rounded-none"
            data-testid="new-category-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Category
          </Button>
        </div>

        {/* Categories Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-32 loading-skeleton"></div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="categories-grid">
            {categories.map((category) => (
              <div 
                key={category.id}
                className="bg-white border border-[#E2E8F0] p-6 hover:border-[#9D6E6D] transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading text-lg text-[#2C2C2C]">{category.name}</h3>
                    <p className="font-body text-xs text-[#767C91]">/{category.slug}</p>
                  </div>
                  <span className="px-2 py-1 bg-[#F2E1E1] text-[#9D6E6D] font-label text-xs">
                    {category.article_count} articles
                  </span>
                </div>
                {category.description && (
                  <p className="font-body text-sm text-[#767C91] mb-4 line-clamp-2">
                    {category.description}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenEdit(category)}
                    className="p-2 text-[#767C91] hover:text-[#9D6E6D] hover:bg-[#F2E1E1]"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteDialog({ open: true, category })}
                    className="p-2 text-[#767C91] hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, category: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editDialog.category ? 'Edit Category' : 'New Category'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">NAME *</label>
              <Input
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Category Name"
                className="rounded-none border-[#E2E8F0]"
                required
                data-testid="category-name-input"
              />
            </div>
            <div>
              <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">SLUG *</label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="category-slug"
                className="rounded-none border-[#E2E8F0]"
                required
                data-testid="category-slug-input"
              />
            </div>
            <div>
              <label className="font-label text-xs text-[#767C91] tracking-widest block mb-2">DESCRIPTION</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                className="rounded-none border-[#E2E8F0]"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialog({ open: false, category: null })}
                className="rounded-none"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#9D6E6D] hover:bg-[#2C2C2C] text-white rounded-none"
                data-testid="save-category-btn"
              >
                {editDialog.category ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, category: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.category?.name}"? Articles in this category will not be deleted but will need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminCategories;
